import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link } from "react-router-dom";
import TextInput from "../components/form/TextInput";
import SelectInput from "../components/form/SelectInput";
import TextArea from "../components/form/TextArea";
import FormSection from "../components/form/FormSection";
import AlertMessage from "../components/form/AlertMessage";
import Modal from "../components/form/Modal";
import axios from "axios";

// Validation Schema using Yup
const schema = yup.object().shape({
  student_name: yup.string().required("Student name is required"),
  surname: yup.string().required("Surname is required"),
  admission_class: yup.string().required("Select a class"),
  address: yup.string().required("Address is required"),
  pincode: yup.string().required("Pincode is required"),
  father_name: yup.string().required("Father name required"),
  father_contact: yup
    .string()
    .matches(/^\d{10}$/, "Enter valid 10-digit contact")
    .required("Father's contact required"),
  father_whatsapp: yup
    .string()
    .matches(/^\d{10}$/, "Enter valid 10-digit WhatsApp")
    .required("Father's WhatsApp required"),
  father_income: yup
    .string()
    .test("is-number", "Income must be a number", (val) =>
      val ? !isNaN(val) : true
    ),
  mother_name: yup.string().required("Mother name required"),
  mother_contact: yup
    .string()
    .matches(/^\d{10}$/, "Enter valid 10-digit contact")
    .required("Mother's contact required"),
  mother_whatsapp: yup
    .string()
    .matches(/^\d{10}$/, "Enter valid 10-digit WhatsApp")
    .required("Mother's WhatsApp required"),
  mother_income: yup
    .string()
    .test("is-number", "Income must be a number", (val) =>
      val ? !isNaN(val) : true
    ),
  sibling_studying: yup.string().required("Select an option"),
  sibling_details: yup.string().when("sibling_studying", {
    is: "Yes",
    then: (schema) => schema.required("Sibling details required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const SECTION_CONFIG = [
  {
    title: "Student Details",
    classname: "border rounded-lg p-20 mb-10 shadow-sm row",
    fields: [
      { type: "text", label: "Student's Name", name: "student_name", placeholder: "Student's Name", col: "col-md-4" },
      { type: "text", label: "Student's Surname", name: "surname", placeholder: "Student's Surname", col: "col-md-4" },
      { type: "select", label: "Admission in Class", name: "admission_class", col: "col-md-4" },
      { type: "textarea", label: "Address", placeholder: "Address", name: "address", col: "col-md-6" },
      { type: "text", label: "Pincode", placeholder: "Pincode", name: "pincode", col: "col-md-4" },
    ],
  },
  {
    title: "Father's Details",
    classname: "border rounded-lg p-20 mb-10 shadow-sm row",
    fields: [
      { type: "text", label: "Father's Name", name: "father_name", placeholder: "Father's Name", col: "col-md-4" },
      { type: "text", label: "Father's Contact No", name: "father_contact", placeholder: "Father's Contact No", col: "col-md-4" },
      { type: "text", label: "Father's WhatsApp No", name: "father_whatsapp", placeholder: "Father's WhatsApp No", col: "col-md-4" },
      { type: "text", label: "Father's Occupation", name: "father_occupation", placeholder: "Father's Occupation", col: "col-md-4" },
      { type: "text", label: "Father's Monthly Income", name: "father_income", placeholder: "Father's Monthly Income", col: "col-md-4" },
    ],
  },
  {
    title: "Mother's Details",
    classname: "border rounded-lg p-20 mb-10 shadow-sm row",
    fields: [
      { type: "text", label: "Mother's Name", name: "mother_name", placeholder: "Mother's Name", col: "col-md-4" },
      { type: "text", label: "Mother's Contact No", name: "mother_contact", placeholder: "Mother's Contact No", col: "col-md-4" },
      { type: "text", label: "Mother's WhatsApp No", name: "mother_whatsapp", placeholder: "Mother's WhatsApp No", col: "col-md-4" },
      { type: "text", label: "Mother's Occupation", name: "mother_occupation", placeholder: "Mother's Occupation", col: "col-md-4" },
      { type: "text", label: "Mother's Monthly Income", name: "mother_income", placeholder: "Mother's Monthly Income", col: "col-md-4" },
    ],
  },
  {
    title: "Additional Information",
    classname: "border rounded-lg p-20 mb-10 shadow-sm row",
    fields: [
      { type: "select", label: "Any other sibling studying in this school?", name: "sibling_studying", col: "col-md-4", options: [{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }] },
      { type: "text", label: "Sibling Details", placeholder: "Sibling Details", name: "sibling_details", col: "col-md-4", conditional: (values) => values.sibling_studying === "Yes" },
    ],
  },
];

export default function StudentEnquiryForm() {
  const [classOptions, setClassOptions] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [highlightedField, setHighlightedField] = useState(null);
  const [showThankYou, setShowThankYou] = useState(false);

  const { control, handleSubmit, watch, reset, setFocus, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
  });
  const sibling_studying = watch("sibling_studying");

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const res = await axios.get("/api/common/classes");
        if (res.data.success) {
          setClassOptions(res.data.data.map(cls => ({ value: cls.id.toString(), label: cls.class_name })));
        } else {
          setErrorMessage("Failed to load classes from server.");
        }
      } catch (err) {
        console.error(err);
        setErrorMessage("Network error while loading class list.");
      } finally {
        setLoadingClasses(false);
      }
    };
    fetchClasses();
  }, []);

  const saveEnquiry = async (formData) => {
    try {
      const res = await axios.post("/api/enquiry/save", formData);
      return res.data;
    } catch (err) {
      console.error(err);
      return { success: false, message: "Server error. Please try again." };
    }
  };

  // Focus, scroll, and highlight
  const focusAndScroll = (fieldName) => {
    setFocus(fieldName);
    const el = document.querySelector(`[name="${fieldName}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedField(fieldName);
      setTimeout(() => setHighlightedField(null), 2000);
    }
  };

  const onSubmit = async (data) => {
    setErrorMessage(""); setSuccessMessage("");

    if (Object.keys(errors).length > 0) {
      focusAndScroll(Object.keys(errors)[0]);
      return;
    }

    const res = await saveEnquiry(data);
    if (res.success) {
      setSuccessMessage("Enquiry saved successfully!");
      reset();
      setShowThankYou(true);
    } else {
      setErrorMessage(res.message || "Failed to save enquiry.");
    }
  };

  return (
    <section className="bg-base">
      <div className="py-32 px-24 d-flex flex-column justify-content-center">
        <div className="mx-auto w-100">
          <Link to="/" className="mb-40 max-w-290-px d-block">
            <img src="/images/logo.png" alt="Logo" />
          </Link>

          <AlertMessage type="danger" message={errorMessage} onClose={() => setErrorMessage("")} />
          <AlertMessage type="success" message={successMessage} onClose={() => setSuccessMessage("")} />

          <form onSubmit={handleSubmit(onSubmit)}>
            <h2 className="text-2xl font-bold mb-6 text-center text-blue-600">
              Student Enquiry Form
            </h2>

            {SECTION_CONFIG.map((section, idx) => (
              <FormSection key={idx} title={section.title} classname={section.classname}>
                {section.fields.map((field) => {
                  if (field.conditional && !field.conditional({ sibling_studying })) return null;

                  return (
                    <div className={field.col} key={field.name}>
                      <Controller
                        name={field.name}
                        control={control}
                        render={({ field: controllerField }) => {
                          const isHighlighted = highlightedField === field.name;

                          const commonProps = {
                            ...controllerField,
                            placeholder: field.placeholder,
                            label: (
                              <>
                                {field.label}
                                {schema.fields[field.name]?._whitelist ? <span className="text-danger">*</span> : ""}
                              </>
                            ),
                            error: errors[field.name]?.message,
                            className: isHighlighted ? "border-red-500 animate-pulse" : "",
                          };

                          if (field.type === "text") return <TextInput {...commonProps} />;
                          if (field.type === "textarea") return <TextArea {...commonProps} />;
                          if (field.type === "select")
                            return (
                              <SelectInput
                                {...commonProps}
                                options={field.options || classOptions}
                                loading={field.name === "admission_class" && loadingClasses}
                              />
                            );
                          return null;
                        }}
                      />
                    </div>
                  );
                })}
              </FormSection>
            ))}

            <div className="flex justify-between mt-4">
              <button
                type="submit"
                className="btn btn-primary text-lg btn-sm px-12 py-16 radius-12 mt-32"
              >
                Save Data Only
              </button>
            </div>
          </form>
        </div>
      </div>
      {/* Thank You Modal */}
      <Modal show={showThankYou} onClose={() => setShowThankYou(false)} title="Thank You!">
        <p>Your enquiry has been submitted successfully.</p>
      </Modal>
    </section>
  );
}
